import { FRONTEND_PTT_RELEASE_TIMEOUT_MS } from "@bezoned/desktop-bridge/ptt-constants.js";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

import { recordErrorInSentry } from "../../utils/init-sentry.js";
import {
  isDeviceAvailable,
  resolveFallbackDeviceId,
} from "./audio-device-selection.js";
import {
  selectPreferredMicrophoneId,
  selectPreferredSpeakerId,
  useDevicesStore,
} from "./audio-devices.store.js";
import { SessionMicrophoneIntentController } from "./session-microphone-intent-controller.js";
import type { MicrophoneController } from "./session-microphone-intent-controller.js";
import { enqueueSerialized } from "./table-call-async.js";
import {
  applyPreferredDevicesForMeeting,
  selectInputDevice,
  selectOutputDevice,
  syncCurrentDevices,
} from "./table-call-audio-devices.js";
import type { MeetingClient, TableCallStatus } from "./table-call.context.js";
import type {
  AudioStateSetter,
  CurrentOperationArgs,
  DeviceSelectionTransactionArgs,
  EnqueueDeviceApplyResult,
  TableCallDiagnostics,
  TableCallRuntime,
  TableCallTarget,
} from "./table-call.types.js";
import {
  AUDIO_PERMISSION_DENIED_MESSAGES,
  INPUT_PERMISSION_MESSAGE,
  MICROPHONE_UNAVAILABLE_MESSAGE,
  OUTPUT_FALLBACK_MESSAGE,
} from "./table-call.types.js";

interface Args {
  meeting: MeetingClient | null;
  status: TableCallStatus;
  runtime: TableCallRuntime;
  setAudioState: AudioStateSetter;
  isCurrentOperation: (args: CurrentOperationArgs) => boolean;
  diagnostics: TableCallDiagnostics;
}

export const useTableCallDeviceController = ({
  meeting,
  status,
  runtime,
  setAudioState,
  isCurrentOperation,
  diagnostics,
}: Args) => {
  const preferredMicrophoneId = useDevicesStore(selectPreferredMicrophoneId);
  const preferredSpeakerId = useDevicesStore(selectPreferredSpeakerId);
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);
  const [canProduceAudio, setCanProduceAudio] = useState(false);
  const [microphoneController, setMicrophoneController] =
    useState<MicrophoneController>(null);
  const microphoneIntentControllerRef = useRef(
    new SessionMicrophoneIntentController()
  );
  const pttTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAppliedPreferenceRef = useRef({
    meeting: null as MeetingClient | null,
    microphoneId: null as string | null,
    speakerId: null as string | null,
  });

  const syncCurrentDevicesFromMeeting = useCallback(
    (targetMeeting: MeetingClient) => {
      const devices = syncCurrentDevices(targetMeeting);
      useDevicesStore
        .getState()
        .setCurrentDevice("audioinput", devices.audioinput);
      useDevicesStore
        .getState()
        .setCurrentDevice("audiooutput", devices.audiooutput);
    },
    []
  );

  const initializeMeetingState = useCallback((client: MeetingClient) => {
    setIsMicrophoneActive(client.self.audioEnabled);
    setCanProduceAudio(client.self.permissions.canProduceAudio === "ALLOWED");
    setMicrophoneController(null);
  }, []);

  const resetDeviceState = useCallback(() => {
    useDevicesStore.getState().setApplying(false);
    useDevicesStore.getState().setCurrentDevice("audioinput", null);
    useDevicesStore.getState().setCurrentDevice("audiooutput", null);
  }, []);

  const stopDeviceApplication = useCallback(() => {
    useDevicesStore.getState().setApplying(false);
  }, []);

  const clearMeetingState = useCallback(() => {
    setIsMicrophoneActive(false);
    setCanProduceAudio(false);
    setMicrophoneController(null);
  }, []);

  const clearPttWatchdog = useCallback(() => {
    if (pttTimeoutRef.current) {
      clearTimeout(pttTimeoutRef.current);
      pttTimeoutRef.current = null;
    }
  }, []);

  const enqueueDeviceApply = async (
    reason: string,
    meetingOverride?: MeetingClient
  ): Promise<EnqueueDeviceApplyResult> => {
    const localMeeting = meetingOverride ?? runtime.meetingRef.current;
    if (!localMeeting) {
      return { status: "skipped" };
    }
    const generation = runtime.operationGenerationRef.current;
    return await enqueueSerialized(runtime.deviceApplyQueueRef, async () => {
      if (
        runtime.meetingRef.current !== localMeeting ||
        runtime.operationGenerationRef.current !== generation
      ) {
        diagnostics.reportDeviceSyncRaceDropped(reason);
        return { status: "skipped" };
      }
      useDevicesStore.getState().setApplying(true);
      useDevicesStore.getState().setLastError(null);
      try {
        const store = useDevicesStore.getState();
        const result = await applyPreferredDevicesForMeeting(
          localMeeting,
          store
        );
        if (
          runtime.meetingRef.current !== localMeeting ||
          runtime.operationGenerationRef.current !== generation
        ) {
          diagnostics.reportDeviceSyncRaceDropped(reason);
          return { status: "skipped" };
        }
        store.setCurrentDevice("audioinput", result.appliedMicrophoneId);
        store.setCurrentDevice("audiooutput", result.appliedSpeakerId);
        if (result.preferredMicrophoneMissing) {
          store.setPreferredDevice("audioinput", result.appliedMicrophoneId);
          recordErrorInSentry(new Error("Preferred microphone missing"), {
            extra: {
              fallbackMicrophoneId: result.appliedMicrophoneId,
              preferredMicrophoneId: store.preferredMicrophoneId,
              reason,
            },
            tags: {
              event: "preferred-device-missing",
              feature: "table-call",
            },
          });
        }
        return {
          appliedMicrophoneId: result.appliedMicrophoneId,
          appliedSpeakerId: result.appliedSpeakerId,
          status: "applied",
        };
      } catch (error) {
        if (
          runtime.meetingRef.current !== localMeeting ||
          runtime.operationGenerationRef.current !== generation
        ) {
          diagnostics.reportDeviceSyncRaceDropped(reason);
          return { status: "failed" };
        }
        useDevicesStore
          .getState()
          .setLastError(
            error instanceof Error ? error.message : "device-apply-failed"
          );
        diagnostics.report(error, {
          classification: diagnostics.classify({
            direction: "unknown",
            error,
            meeting: localMeeting,
            operation: "post-join-device-apply",
            preferredDeviceId:
              useDevicesStore.getState().preferredMicrophoneId ??
              useDevicesStore.getState().preferredSpeakerId,
          }),
          event: "device-apply-failed",
          extra: { reason },
        });
        return { status: "failed" };
      } finally {
        if (
          runtime.meetingRef.current === localMeeting &&
          runtime.operationGenerationRef.current === generation
        ) {
          useDevicesStore.getState().setApplying(false);
        }
      }
    });
  };
  const enqueueDeviceApplyEvent = useEffectEvent(enqueueDeviceApply);

  const enableLocalMicrophone = useCallback(
    async (
      targetMeeting: MeetingClient,
      target: TableCallTarget,
      generation: number
    ) => {
      const scope = { generation, session: targetMeeting };
      const intent = microphoneIntentControllerRef.current.capture(scope);
      if (!intent) {
        return true;
      }
      const isIntentCurrent = () =>
        microphoneIntentControllerRef.current.isCurrent(intent) &&
        isCurrentOperation({ generation, target, targetMeeting });
      if (targetMeeting.self.permissions.canProduceAudio !== "ALLOWED") {
        microphoneIntentControllerRef.current.request(scope, false);
        setMicrophoneController(null);
        useDevicesStore.getState().setLastError(INPUT_PERMISSION_MESSAGE);
        return false;
      }
      try {
        await targetMeeting.self.enableAudio();
        if (!isIntentCurrent()) {
          return false;
        }
        return true;
      } catch (error) {
        if (!isIntentCurrent()) {
          return false;
        }
        diagnostics.report(error, {
          classification: diagnostics.classify({
            deviceKind: "audioinput",
            direction: "publish",
            error,
            meeting: targetMeeting,
            operation: "microphone-enable",
            preferredDeviceId: useDevicesStore.getState().preferredMicrophoneId,
            target,
          }),
          event: "microphone-restore-failed",
        });
        microphoneIntentControllerRef.current.request(scope, false);
        setMicrophoneController(null);
        clearPttWatchdog();
        useDevicesStore.getState().setLastError(MICROPHONE_UNAVAILABLE_MESSAGE);
        return false;
      }
    },
    [clearPttWatchdog, diagnostics, isCurrentOperation]
  );

  const canEnableMicrophoneNow = () => {
    const client = runtime.meetingRef.current;
    const target = runtime.activeTargetRef.current;
    return (
      Boolean(client?.self) &&
      client?.self.permissions.canProduceAudio === "ALLOWED" &&
      runtime.statusRef.current === "connected" &&
      target?.key === runtime.desiredTargetRef.current?.key
    );
  };
  const setMicrophoneActive = async (
    active: boolean,
    controller: MicrophoneController = active
      ? microphoneIntentControllerRef.current.controller
      : null
  ) => {
    const client = runtime.meetingRef.current;
    if (!client?.self) {
      microphoneIntentControllerRef.current.invalidate();
      throw new Error("microphone-control-not-available");
    }
    const intent = microphoneIntentControllerRef.current.request(
      {
        generation: runtime.operationGenerationRef.current,
        session: client,
      },
      active,
      controller
    );
    if (!active) {
      // A final off transition releases the controller before SDK teardown settles.
      clearPttWatchdog();
      setMicrophoneController(null);
    }
    const performToggle = async (): Promise<void> => {
      if (runtime.meetingRef.current !== client || !client.self) {
        throw new Error("microphone-control-not-available");
      }
      const isToggleCurrent = () =>
        runtime.meetingRef.current === client &&
        runtime.operationGenerationRef.current === intent.generation &&
        microphoneIntentControllerRef.current.isCurrent(intent);
      if (active && !canEnableMicrophoneNow()) {
        microphoneIntentControllerRef.current.request(
          { generation: intent.generation, session: client },
          false
        );
        setMicrophoneController(null);
        setIsMicrophoneActive(active ? false : client.self.audioEnabled);
        throw new Error("microphone-enable-blocked");
      }
      if (!microphoneIntentControllerRef.current.isCurrent(intent)) {
        return;
      }
      try {
        if (active) {
          const target = runtime.activeTargetRef.current;
          if (!target) {
            throw new Error("microphone-enable-target-missing");
          }
          const published = await enableLocalMicrophone(
            client,
            target,
            intent.generation
          );
          if (!isToggleCurrent()) {
            return;
          }
          if (!published) {
            throw new Error("microphone-publish-unhealthy");
          }
          setIsMicrophoneActive(client.self.audioEnabled);
        } else {
          await client.self.disableAudio();
          if (!isToggleCurrent()) {
            return;
          }
          setIsMicrophoneActive(client.self.audioEnabled);
          setMicrophoneController(null);
          clearPttWatchdog();
        }
      } catch (error) {
        if (!isToggleCurrent()) {
          return;
        }
        diagnostics.report(error, {
          classification: diagnostics.classify({
            deviceKind: "audioinput",
            direction: "input",
            error,
            meeting: client,
            operation: active ? "microphone-enable" : "microphone-disable",
            preferredDeviceId: useDevicesStore.getState().preferredMicrophoneId,
          }),
          event: "microphone-toggle-failed",
          extra: { action: active ? "enable" : "disable" },
        });
        setIsMicrophoneActive(active ? false : client.self.audioEnabled);
        if (active || !client.self.audioEnabled) {
          if (active) {
            microphoneIntentControllerRef.current.request(
              { generation: intent.generation, session: client },
              false
            );
          }
          setMicrophoneController(null);
          clearPttWatchdog();
        }
        throw error;
      }
    };
    await microphoneIntentControllerRef.current.enqueue(intent, performToggle);
    if (microphoneIntentControllerRef.current.isCurrent(intent)) {
      setMicrophoneController(intent.controller);
    }
  };

  const setMicrophoneAlwaysOn = async (enabled: boolean) =>
    await setMicrophoneActive(enabled, enabled ? "always-on" : null);

  const stopPushToTalk = async () => {
    clearPttWatchdog();
    if (microphoneIntentControllerRef.current.controller !== "ptt") {
      return;
    }
    await setMicrophoneActive(false);
  };

  const startPushToTalk = async () => {
    if (microphoneIntentControllerRef.current.controller === "always-on") {
      return;
    }
    clearPttWatchdog();
    pttTimeoutRef.current = setTimeout(async () => {
      pttTimeoutRef.current = null;
      try {
        await stopPushToTalk();
      } catch (error) {
        recordErrorInSentry(error);
      }
    }, FRONTEND_PTT_RELEASE_TIMEOUT_MS);
    if (microphoneIntentControllerRef.current.controller === "ptt") {
      return;
    }
    await setMicrophoneActive(true, "ptt");
  };

  const performPreferredAudioDeviceTransaction = async ({
    kind,
    deviceId,
  }: DeviceSelectionTransactionArgs) => {
    const client = runtime.meetingRef.current;
    if (!client) {
      useDevicesStore.getState().setPreferredDevice(kind, deviceId);
      useDevicesStore.getState().setCurrentDevice(kind, deviceId);
      return;
    }
    const target = runtime.activeTargetRef.current;
    const generation = runtime.operationGenerationRef.current;
    const isCurrent = () =>
      runtime.meetingRef.current === client &&
      runtime.operationGenerationRef.current === generation &&
      runtime.activeTargetRef.current === target;
    const previousPreferred =
      kind === "audioinput"
        ? useDevicesStore.getState().preferredMicrophoneId
        : useDevicesStore.getState().preferredSpeakerId;
    useDevicesStore.getState().setApplying(true);
    useDevicesStore.getState().setLastError(null);
    try {
      await (kind === "audioinput"
        ? selectInputDevice({ deviceId, meeting: client })
        : selectOutputDevice({ deviceId, meeting: client }));
      if (!isCurrent()) {
        return;
      }
      useDevicesStore.getState().setCurrentDevice(kind, deviceId);
      if (
        kind === "audioinput" &&
        microphoneIntentControllerRef.current.capture({
          generation,
          session: client,
        })
      ) {
        if (!target) {
          throw new Error("microphone-selection-target-missing");
        }
        if (!(await enableLocalMicrophone(client, target, generation))) {
          throw new Error("microphone-selection-publish-failed");
        }
        if (!isCurrent()) {
          return;
        }
      }
      useDevicesStore.getState().setPreferredDevice(kind, deviceId);
    } catch (error) {
      if (!isCurrent()) {
        return;
      }
      diagnostics.report(error, {
        classification: diagnostics.classify({
          deviceKind: kind,
          direction: kind === "audioinput" ? "input" : "output",
          error,
          meeting: client,
          operation:
            kind === "audioinput"
              ? "microphone-selection"
              : "speaker-selection",
          preferredDeviceId: previousPreferred,
          target,
        }),
        event:
          kind === "audioinput"
            ? "microphone-selection-failed"
            : "speaker-selection-failed",
      });
      useDevicesStore
        .getState()
        .setLastError(
          kind === "audioinput"
            ? MICROPHONE_UNAVAILABLE_MESSAGE
            : OUTPUT_FALLBACK_MESSAGE
        );
      throw error;
    } finally {
      if (isCurrent()) {
        useDevicesStore.getState().setApplying(false);
      }
    }
  };

  const setPreferredAudioDeviceTransactional = async (
    args: DeviceSelectionTransactionArgs
  ) => {
    if (args.kind === "audiooutput") {
      await performPreferredAudioDeviceTransaction(args);
      return;
    }
    const client = runtime.meetingRef.current;
    if (!client) {
      await performPreferredAudioDeviceTransaction(args);
      return;
    }
    const intent = microphoneIntentControllerRef.current.snapshot({
      generation: runtime.operationGenerationRef.current,
      session: client,
    });
    await microphoneIntentControllerRef.current.enqueue(intent, async () => {
      await performPreferredAudioDeviceTransaction(args);
    });
  };

  useEffect(() => {
    if (!meeting) {
      lastAppliedPreferenceRef.current = {
        meeting: null,
        microphoneId: null,
        speakerId: null,
      };
      useDevicesStore.getState().setApplying(false);
      useDevicesStore.getState().setCurrentDevice("audioinput", null);
      useDevicesStore.getState().setCurrentDevice("audiooutput", null);
      return;
    }
    const generation = runtime.operationGenerationRef.current;
    const isCurrent = () =>
      runtime.meetingRef.current === meeting &&
      runtime.operationGenerationRef.current === generation;
    const audioUpdate = ({ audioEnabled }: { audioEnabled: boolean }) => {
      if (!isCurrent()) {
        return;
      }
      setIsMicrophoneActive(audioEnabled);
    };
    const permissionsUpdate = () => {
      if (isCurrent()) {
        setCanProduceAudio(
          meeting.self.permissions.canProduceAudio === "ALLOWED"
        );
      }
    };
    meeting.self.addListener("audioUpdate", audioUpdate);
    meeting.self.permissions.addListener(
      "permissionsUpdate",
      permissionsUpdate
    );
    return () => {
      meeting.self.removeListener("audioUpdate", audioUpdate);
      meeting.self.permissions.removeListener(
        "permissionsUpdate",
        permissionsUpdate
      );
    };
  }, [meeting, runtime]);

  useEffect(() => {
    if (!(meeting && status === "connected")) {
      return;
    }
    const generation = runtime.operationGenerationRef.current;
    const isCurrent = () =>
      runtime.meetingRef.current === meeting &&
      runtime.operationGenerationRef.current === generation;
    const last = lastAppliedPreferenceRef.current;
    if (
      last.meeting === meeting &&
      last.microphoneId === preferredMicrophoneId &&
      last.speakerId === preferredSpeakerId
    ) {
      return;
    }
    lastAppliedPreferenceRef.current = {
      meeting,
      microphoneId: preferredMicrophoneId,
      speakerId: preferredSpeakerId,
    };
    const applyPreferredDevices = async () => {
      try {
        await enqueueDeviceApplyEvent("preferred-device-change");
      } catch (error) {
        if (!isCurrent()) {
          return;
        }
        recordErrorInSentry(error, {
          extra: { reason: "preferred-device-change" },
          tags: { event: "device-apply-failed", feature: "table-call" },
        });
      }
    };
    void applyPreferredDevices();
  }, [meeting, preferredMicrophoneId, preferredSpeakerId, runtime, status]);

  useEffect(() => {
    if (!meeting) {
      return;
    }
    let disposed = false;
    const generation = runtime.operationGenerationRef.current;
    const isCurrent = () =>
      !disposed &&
      runtime.meetingRef.current === meeting &&
      runtime.operationGenerationRef.current === generation;
    const onDeviceUpdate = ({ device }: { device: MediaDeviceInfo }) => {
      if (
        isCurrent() &&
        (device.kind === "audioinput" || device.kind === "audiooutput")
      ) {
        useDevicesStore
          .getState()
          .setCurrentDevice(device.kind, device.deviceId);
      }
    };
    const onDeviceListUpdate = async () => {
      if (!isCurrent()) {
        return;
      }
      const [microphones, speakers] = await Promise.all([
        meeting.self.getAudioDevices(),
        meeting.self.getSpeakerDevices(),
      ]);
      if (!isCurrent()) {
        return;
      }
      const store = useDevicesStore.getState();
      const entries = [
        [
          "audioinput",
          microphones,
          store.preferredMicrophoneId,
          resolveFallbackDeviceId(microphones),
        ],
        [
          "audiooutput",
          speakers,
          store.preferredSpeakerId,
          resolveFallbackDeviceId(speakers),
        ],
      ] as const;
      for (const [kind, devices, preferred, fallback] of entries) {
        if (!(preferred && !isDeviceAvailable(devices, preferred))) {
          continue;
        }
        recordErrorInSentry(
          new Error(
            `Preferred ${kind === "audioinput" ? "microphone" : "speaker"} missing`
          ),
          {
            extra:
              kind === "audioinput"
                ? {
                    fallbackMicrophoneId: fallback,
                    preferredMicrophoneId: preferred,
                  }
                : {
                    fallbackSpeakerId: fallback,
                    preferredSpeakerId: preferred,
                  },
            tags: { event: "preferred-device-missing", feature: "table-call" },
          }
        );
        const applyMissingDeviceFallback = async () => {
          try {
            await enqueueDeviceApplyEvent(
              `preferred-${kind === "audioinput" ? "microphone" : "speaker"}-missing`
            );
          } catch (error) {
            if (!isCurrent()) {
              return;
            }
            diagnostics.report(error, {
              classification: diagnostics.classify({
                deviceKind: kind,
                direction: kind === "audioinput" ? "input" : "output",
                error,
                fallbackDeviceId: fallback,
                meeting,
                operation: "selected-device-missing-fallback",
                preferredDeviceId: preferred,
              }),
              event: "device-apply-failed",
            });
          }
        };
        void applyMissingDeviceFallback();
      }
    };
    const onPermissionUpdate = (payload: {
      kind: "audio" | "video" | "screenshare";
      message: string;
    }) => {
      if (!isCurrent() || payload.kind !== "audio") {
        return;
      }
      const denied = AUDIO_PERMISSION_DENIED_MESSAGES.has(payload.message);
      const audioPermissionAllowed =
        meeting.self.permissions.canProduceAudio === "ALLOWED";
      setCanProduceAudio(audioPermissionAllowed);
      useDevicesStore.getState().setHasPermissions(audioPermissionAllowed);
      if (denied) {
        microphoneIntentControllerRef.current.request(
          {
            generation: runtime.operationGenerationRef.current,
            session: meeting,
          },
          false
        );
        setMicrophoneController(null);
        clearPttWatchdog();
        useDevicesStore
          .getState()
          .setLastError(
            "Microphone access is denied by the browser or system."
          );
      } else if (payload.message === "ACCEPTED") {
        useDevicesStore.getState().setLastError(null);
      }
    };
    const onPermissionError = (payload: {
      kind: "audio" | "video" | "screenshare";
      message: string;
      constraints: unknown;
    }) => {
      if (!isCurrent() || payload.kind !== "audio") {
        return;
      }
      microphoneIntentControllerRef.current.request(
        {
          generation: runtime.operationGenerationRef.current,
          session: meeting,
        },
        false
      );
      setMicrophoneController(null);
      clearPttWatchdog();
      setCanProduceAudio(false);
      useDevicesStore.getState().setHasPermissions(false);
      useDevicesStore.getState().setLastError(INPUT_PERMISSION_MESSAGE);
      const error = new Error("RTK media permission error");
      diagnostics.report(error, {
        classification: diagnostics.classify({
          deviceKind: "audioinput",
          direction: "input",
          error,
          meeting,
          operation: "media-permission-error",
          rtkPermissionMessage: payload.message,
        }),
        event: "RTK media permission error",
        extra: payload,
      });
    };
    const onRoomJoined = ({ reconnected }: { reconnected: boolean }) => {
      if (isCurrent()) {
        const applyJoinedDevices = async () => {
          const reason = reconnected
            ? "room-joined-reconnected"
            : "room-joined";
          try {
            await enqueueDeviceApplyEvent(reason);
          } catch (error) {
            if (!isCurrent()) {
              return;
            }
            recordErrorInSentry(error, {
              extra: { reason },
              tags: { event: "device-apply-failed", feature: "table-call" },
            });
          }
        };
        void applyJoinedDevices();
      }
    };
    if (!isCurrent()) {
      return;
    }
    syncCurrentDevicesFromMeeting(meeting);
    useDevicesStore
      .getState()
      .setHasPermissions(
        meeting.self.permissions.canProduceAudio === "ALLOWED"
      );
    meeting.self.addListener("deviceUpdate", onDeviceUpdate);
    meeting.self.addListener("deviceListUpdate", onDeviceListUpdate);
    meeting.self.addListener("mediaPermissionUpdate", onPermissionUpdate);
    meeting.self.addListener("mediaPermissionError", onPermissionError);
    meeting.self.addListener("roomJoined", onRoomJoined);
    const syncInitialDeviceList = async () => {
      try {
        await onDeviceListUpdate();
      } catch (error) {
        if (!isCurrent()) {
          return;
        }
        recordErrorInSentry(error, {
          extra: { reason: "initial-device-list-sync" },
          tags: { event: "device-sync-race-dropped", feature: "table-call" },
        });
      }
    };
    void syncInitialDeviceList();
    return () => {
      disposed = true;
      meeting.self.removeListener("deviceUpdate", onDeviceUpdate);
      meeting.self.removeListener("deviceListUpdate", onDeviceListUpdate);
      meeting.self.removeListener("mediaPermissionUpdate", onPermissionUpdate);
      meeting.self.removeListener("mediaPermissionError", onPermissionError);
      meeting.self.removeListener("roomJoined", onRoomJoined);
    };
  }, [
    diagnostics,
    meeting,
    runtime,
    clearPttWatchdog,
    syncCurrentDevicesFromMeeting,
  ]);

  return {
    canControlMicrophone: canEnableMicrophoneNow() || isMicrophoneActive,
    canEnableMicrophone:
      canProduceAudio &&
      status === "connected" &&
      Boolean(runtime.meetingRef.current?.self) &&
      runtime.activeTargetRef.current?.key ===
        runtime.desiredTargetRef.current?.key,
    clearMeetingState,
    enqueueDeviceApply,
    initializeMeetingState,
    isMicrophoneDesired: () =>
      Boolean(
        microphoneIntentControllerRef.current.capture({
          generation: runtime.operationGenerationRef.current,
          session: runtime.meetingRef.current,
        })
      ),
    microphoneActive: isMicrophoneActive,
    microphoneAlwaysOn: microphoneController === "always-on",
    prepareJoinedMeeting: async (
      client: MeetingClient,
      target: TableCallTarget,
      generation: number
    ) => {
      syncCurrentDevicesFromMeeting(client);
      setAudioState("applying-devices");
      const result = await enqueueDeviceApply("post-join", client);
      if (result.status === "applied") {
        lastAppliedPreferenceRef.current = {
          meeting: client,
          microphoneId: useDevicesStore.getState().preferredMicrophoneId,
          speakerId: useDevicesStore.getState().preferredSpeakerId,
        };
      }
      if (!isCurrentOperation({ generation, target, targetMeeting: client })) {
        return null;
      }
      setAudioState("connected");
      return await enableLocalMicrophone(client, target, generation);
    },
    resetDeviceState,
    resetMicrophoneState: () => {
      microphoneIntentControllerRef.current.invalidate();
      clearPttWatchdog();
      setMicrophoneController(null);
      setIsMicrophoneActive(false);
      setCanProduceAudio(false);
    },
    setMicrophoneActive,
    setMicrophoneAlwaysOn,
    setPreferredMicrophone: async (deviceId: string) =>
      await setPreferredAudioDeviceTransactional({
        deviceId,
        kind: "audioinput",
      }),
    setPreferredSpeaker: async (deviceId: string) =>
      await setPreferredAudioDeviceTransactional({
        deviceId,
        kind: "audiooutput",
      }),
    startPushToTalk,
    stopDeviceApplication,
    stopPushToTalk,
  };
};
